import {
  Box,
  Button,
  type ButtonProps,
  Center,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import type { KeyboardEvent, ReactNode } from 'react';
import bannerLight from '../../assets/logos/bannerLight.svg';
import bannerDark from '../../assets/logos/bannerDark.svg';
import { VIEWPORT_MIN_H } from '@/constants/mobileLayout';

export function useAuthTheme() {
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const hintBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const hintBorder = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const linkColor = useColorModeValue('red.600', 'red.300');
  const dividerColor = useColorModeValue('gray.300', 'whiteAlpha.300');

  return {
    cardBg,
    cardBorder,
    mutedText,
    hintBg,
    hintBorder,
    linkColor,
    dividerColor,
  };
}

export function AuthLoadingScreen(props: { message: string }) {
  const heroSrc = useColorModeValue(bannerLight, bannerDark);
  const { mutedText } = useAuthTheme();

  return (
    <Center w='full' minH={VIEWPORT_MIN_H}>
      <VStack spacing={6} px={4}>
        <Image src={heroSrc} height={12} alt='10 Teams' />
        <Spinner size='lg' color='red.500' thickness='3px' />
        <Text fontSize='md' color={mutedText} textAlign='center' maxW='xs'>
          {props.message}
        </Text>
      </VStack>
    </Center>
  );
}

export function AuthPageLayout(props: {
  children: ReactNode;
  backTo?: string;
  backLabel?: string;
  onBack?: () => void;
  showLogo?: boolean;
}) {
  const heroSrc = useColorModeValue(bannerLight, bannerDark);
  const { cardBg, cardBorder } = useAuthTheme();

  return (
    <Center w='full' minH={VIEWPORT_MIN_H} px={4} py={{ base: 6, md: 10 }} position='relative'>
      {(props.backTo || props.onBack) && (
        props.backTo ? (
          <Link to={props.backTo}>
            <IconButton
              position='absolute'
              top={{ base: 4, md: 8 }}
              left={{ base: 4, md: 8 }}
              aria-label={props.backLabel ?? 'Go back'}
              variant='ghost'
              size='sm'
              icon={
                <HStack spacing={1}>
                  <ArrowBackIcon />
                  <Text fontSize='sm' fontWeight={500} display={{ base: 'none', sm: 'block' }}>
                    {props.backLabel ?? 'Back'}
                  </Text>
                </HStack>
              }
            />
          </Link>
        ) : (
          <IconButton
            position='absolute'
            top={{ base: 4, md: 8 }}
            left={{ base: 4, md: 8 }}
            aria-label={props.backLabel ?? 'Go back'}
            variant='ghost'
            size='sm'
            onClick={props.onBack}
            icon={
              <HStack spacing={1}>
                <ArrowBackIcon />
                <Text fontSize='sm' fontWeight={500} display={{ base: 'none', sm: 'block' }}>
                  {props.backLabel ?? 'Back'}
                </Text>
              </HStack>
            }
          />
        )
      )}
      <VStack spacing={6} w='full' maxW='md'>
        {props.showLogo !== false && <Image src={heroSrc} height={12} alt='10 Teams' />}
        <Box
          w='full'
          bg={cardBg}
          borderWidth='1px'
          borderColor={cardBorder}
          borderRadius='xl'
          boxShadow='lg'
          px={{ base: 5, md: 8 }}
          py={{ base: 6, md: 8 }}
        >
          {props.children}
        </Box>
      </VStack>
    </Center>
  );
}

export function AuthHeading(props: { children: ReactNode }) {
  return (
    <Heading
      fontSize={{ base: 'xl', md: '2xl' }}
      fontWeight='semibold'
      letterSpacing='tight'
      mb={1}
    >
      {props.children}
    </Heading>
  );
}

export function AuthSubtext(props: {
  children: ReactNode;
  textAlign?: 'left' | 'center' | 'right';
  mb?: number | string;
}) {
  const { mutedText } = useAuthTheme();
  return (
    <Text fontSize='sm' color={mutedText} lineHeight='tall' textAlign={props.textAlign} mb={props.mb}>
      {props.children}
    </Text>
  );
}

export function AuthSwitchPrompt(props: { children: ReactNode; actionLabel: string; onAction: () => void }) {
  const { mutedText, linkColor } = useAuthTheme();
  return (
    <Text fontSize='sm' color={mutedText} mb={4}>
      {props.children}{' '}
      <Button
        variant='link'
        color={linkColor}
        fontWeight='semibold'
        fontSize='sm'
        verticalAlign='baseline'
        onClick={props.onAction}
        _hover={{ textDecoration: 'underline' }}
      >
        {props.actionLabel}
      </Button>
    </Text>
  );
}

export function PrimaryAuthButton(props: ButtonProps) {
  return (
    <Button
      width='full'
      size='md'
      bg='red.500'
      color='white'
      fontWeight='semibold'
      borderRadius='md'
      _hover={{ bg: 'red.600' }}
      _active={{ bg: 'red.700' }}
      _disabled={{ bg: 'gray.300', color: 'gray.500', cursor: 'not-allowed' }}
      {...props}
    />
  );
}

export function AuthTextButton(props: ButtonProps) {
  const { linkColor } = useAuthTheme();
  return (
    <Button
      variant='link'
      size='sm'
      color={linkColor}
      fontWeight='medium'
      _hover={{ textDecoration: 'underline' }}
      {...props}
    />
  );
}

export function AuthMutedTextButton(props: ButtonProps) {
  const faintText = useColorModeValue('gray.500', 'gray.400');
  return (
    <Button
      variant='link'
      size='sm'
      color={faintText}
      fontWeight='normal'
      _hover={{ color: faintText, textDecoration: 'underline' }}
      {...props}
    />
  );
}

export function NeutralAuthButton(props: ButtonProps) {
  const bg = useColorModeValue('gray.900', 'white');
  const color = useColorModeValue('white', 'gray.900');
  const hoverBg = useColorModeValue('gray.800', 'gray.100');
  const activeBg = useColorModeValue('gray.700', 'gray.200');

  return (
    <Button
      width='full'
      size='md'
      bg={bg}
      color={color}
      fontWeight='semibold'
      borderRadius='md'
      _hover={{ bg: hoverBg }}
      _active={{ bg: activeBg }}
      _disabled={{ bg: 'gray.300', color: 'gray.500', cursor: 'not-allowed' }}
      {...props}
    />
  );
}

export function AuthPasswordField(props: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  visible: boolean;
  onToggleVisible: () => void;
  autoComplete?: string;
  onEnter?: () => void;
  helperAction?: { label: string; onClick: () => void };
}) {
  return (
    <InputGroup size='md'>
      <Input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        type={props.visible ? 'text' : 'password'}
        autoComplete={props.autoComplete}
        borderRadius='md'
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') props.onEnter?.();
        }}
      />
      <InputRightElement width='4.5rem'>
        <Button
          h='1.75rem'
          size='sm'
          variant='ghost'
          colorScheme='gray'
          onClick={props.onToggleVisible}
        >
          {props.visible ? 'Hide' : 'Show'}
        </Button>
      </InputRightElement>
    </InputGroup>
  );
}

export function EmailDeliveryHint() {
  const { mutedText, hintBg, hintBorder, linkColor } = useAuthTheme();

  return (
    <Box
      w='full'
      px={3}
      py={3}
      borderRadius='md'
      bg={hintBg}
      borderWidth='1px'
      borderColor={hintBorder}
    >
      <Text fontSize='sm' textAlign='center' color={mutedText} lineHeight='tall'>
        Can&apos;t find the email?{' '}
        <Text as='span' fontWeight='bold' color={linkColor}>
          Check your spam or junk folder
        </Text>
        {' '}
        — links from 10 Teams often land there.
      </Text>
    </Box>
  );
}
